import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormInputComponent } from '../../utils/form-input/form-input.component';
import { DatepickerComponent } from '../../utils/datepicker/datepicker.component';
import { FormSelectComponent } from '../../utils/form-select/form-select.component';
import { MdbTooltipModule } from 'mdb-angular-ui-kit/tooltip';
import { ListPaginationBarComponent } from '../../utils/list-pagination-bar/list-pagination-bar.component';
import { PaginationMeta } from '../../../models/pagination-meta.model';
import { EnumSelectComponent } from '../../utils/enum-select/enum-select.component';
import { SpinnerComponent } from '../../utils/spinner/spinner.component';
import { MdbButtonComponent } from '../../utils/mdb-button/mdb-button.component';
import { MailService } from '../../../services/mailarchiv/mail.service';
import { AddressService } from '../../../services/mailarchiv/address.service';
import { HttpErrorsService } from '../../../services/utils/http-errors.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  MailAccountForSearch,
  MailHit,
  MailSearchParams,
  MailSearchResult,
} from '../../../models/mailarchiv/mail.model';

const SPAM_MAP: Record<string, number> = {
  exclude: 4,
  some: 6,
  include: 10,
};

@Component({
  selector: 'app-mail-search',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    FormInputComponent,
    DatepickerComponent,
    FormSelectComponent,
    MdbTooltipModule,
    ListPaginationBarComponent,
    EnumSelectComponent,
    SpinnerComponent,
    MdbButtonComponent,
  ],
  templateUrl: './mail-search.component.html',
  styleUrl: './mail-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailSearchComponent implements OnInit {
  form: FormGroup;
  loading = signal(false);
  searchResult = signal<MailSearchResult | null>(null);
  expandedId = signal<string | null>(null);
  fromOptions = signal<{ Address: string }[]>([]);
  toOptions = signal<{ Address: string }[]>([]);
  mailAccountOptions = signal<MailAccountForSearch[]>([]);
  fromOptionsLoading = signal(false);
  toOptionsLoading = signal(false);
  /** Mail ID for which EML download is in progress. */
  downloadingEmlId = signal<string | null>(null);
  /** Key 'mailId|attachmentName' for which attachment download is in progress. */
  downloadingAttachmentKey = signal<string | null>(null);

  private fromSearch$ = new Subject<string>();
  private toSearch$ = new Subject<string>();
  private readonly destroyRef = inject(DestroyRef);

  readonly displayDateTimeFormat = environment.displayDateTimeFormat;
  readonly hits = computed(() => this.searchResult()?.hits ?? []);
  readonly totalHits = computed(() => this.searchResult()?.estimatedTotalHits ?? 0);
  readonly limit = computed(() => this.searchResult()?.limit ?? 25);
  readonly offset = computed(() => this.searchResult()?.offset ?? 0);
  readonly currentPage = computed(() => {
    const lim = this.limit();
    const off = this.offset();
    return lim > 0 ? Math.floor(off / lim) + 1 : 1;
  });
  readonly totalPages = computed(() => {
    const total = this.totalHits();
    const lim = this.limit();
    return lim > 0 ? Math.ceil(total / lim) : 0;
  });
  readonly hasPrevPage = computed(() => this.offset() > 0);
  readonly hasNextPage = computed(() => {
    const res = this.searchResult();
    if (!res) return false;
    return res.offset + res.hits.length < res.estimatedTotalHits;
  });

  readonly perPageOptions = [10, 25, 50, 100];

  readonly spamIncludeOptions = { exclude: 'exclude', some: 'some', include: 'include' } as const;
  readonly spamIncludeLabelKey = (v: string): string =>
    v === 'exclude' ? 'MAILARCHIV.SPAM_LOW' : v === 'some' ? 'MAILARCHIV.SPAM_MEDIUM' : 'MAILARCHIV.SPAM_HIGH';
  readonly dateOrderOptions = { desc: 'desc', asc: 'asc' } as const;
  readonly dateOrderLabelKey = (v: string): string =>
    v === 'desc' ? 'MAILARCHIV.DATE_DESC' : 'MAILARCHIV.DATE_ASC';

  constructor(
    private fb: FormBuilder,
    private mailService: MailService,
    private addressService: AddressService,
    private httpErrors: HttpErrorsService,
    private datePipe: DatePipe,
    private sanitizer: DomSanitizer
  ) {
    this.form = this.fb.group({
      subject: [''],
      from: [''],
      to: [''],
      since_date: [''],
      until_date: [''],
      spam_include: ['exclude'],
      date_order: ['desc'],
      limit: [25],
      offset: [0],
      mail_account_ids: [[] as number[]],
    });
    this.fromSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((q) => this.fetchFromOptions(q));
    this.toSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((q) => this.fetchToOptions(q));
  }

  ngOnInit(): void {
    this.mailService.getMailAccountsForSearch()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((accounts) => this.mailAccountOptions.set(accounts));
    this.search();
  }

  loadFromOptions(searchText: string): void {
    if (searchText === '') {
      this.fetchFromOptions('');
    } else {
      this.fromSearch$.next(searchText);
    }
  }

  loadToOptions(searchText: string): void {
    if (searchText === '') {
      this.fetchToOptions('');
    } else {
      this.toSearch$.next(searchText);
    }
  }

  private fetchFromOptions(searchText: string): void {
    this.fromOptionsLoading.set(true);
    this.addressService
      .search({ address: searchText || '', from_bool: 1 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const opts = (res.hits ?? [])
            .map((h) => {
              const addr = h.address ?? (h as Record<string, string>)['Address'] ?? '';
              return addr ? { Address: addr } : null;
            })
            .filter((o): o is { Address: string } => o != null);
          this.fromOptions.set([...new Map(opts.map((o) => [o.Address, o])).values()]);
        },
        complete: () => this.fromOptionsLoading.set(false),
      });
  }

  private fetchToOptions(searchText: string): void {
    this.toOptionsLoading.set(true);
    this.addressService
      .search({ address: searchText || '', to_bool: 1 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const opts = (res.hits ?? [])
            .map((h) => {
              const addr = h.address ?? (h as Record<string, string>)['Address'] ?? '';
              return addr ? { Address: addr } : null;
            })
            .filter((o): o is { Address: string } => o != null);
          this.toOptions.set([...new Map(opts.map((o) => [o.Address, o])).values()]);
        },
        complete: () => this.toOptionsLoading.set(false),
      });
  }

  getControl(name: string): AbstractControl | null {
    return this.form.get(name);
  }

  private parseDateToTimestamp(dateStr: string, endOfDay: boolean): number {
    if (!dateStr) {
      return endOfDay
        ? Math.floor(new Date().setHours(23, 59, 59, 999) / 1000)
        : Math.floor(new Date('1970-01-01').getTime() / 1000);
    }
    let d: Date;
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      d = new Date(dateStr + (endOfDay ? 'T23:59:59' : 'T00:00:00'));
    } else {
      const parts = dateStr.split(/[.\/]/);
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        d = new Date(year, month, day, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0);
      } else {
        d = new Date(dateStr);
      }
    }
    return Math.floor(d.getTime() / 1000);
  }

  private dateToUnixStartOfDay(dateStr: string): number {
    return this.parseDateToTimestamp(dateStr, false);
  }

  private dateToUnixEndOfDay(dateStr: string): number {
    if (!dateStr) {
      const t = new Date();
      t.setDate(t.getDate() + 1);
      t.setHours(0, 0, 0, 0);
      return Math.floor(t.getTime() / 1000) - 1;
    }
    return this.parseDateToTimestamp(dateStr, true);
  }

  buildSearchParams(offsetOverride?: number): MailSearchParams {
    const v = this.form.value;
    const spamInclude = v.spam_include ?? 'exclude';
    const params: MailSearchParams = {
      subject: v.subject || undefined,
      from_email: v.from || undefined,
      to_email: v.to || undefined,
      from_exact: true,
      to_exact: true,
      since_date: this.dateToUnixStartOfDay(v.since_date),
      until_date: this.dateToUnixEndOfDay(v.until_date),
      limit: v.limit ?? 25,
      offset: offsetOverride ?? v.offset ?? 0,
      spam_score: SPAM_MAP[spamInclude] ?? 4,
      date_order: (v.date_order as 'asc' | 'desc') ?? 'desc',
    };
    const ids = (v.mail_account_ids as number[])?.filter((id: unknown) => typeof id === 'number' && id >= 1);
    if (ids?.length) {
      params.mail_account_ids = ids;
    }
    return params;
  }

  search(offsetOverride?: number): void {
    this.form.patchValue({ offset: offsetOverride ?? 0 }, { emitEvent: false });
    const params = this.buildSearchParams(offsetOverride);
    this.loading.set(true);
    this.mailService.search(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => this.searchResult.set(result),
        error: (err) => this.httpErrors.handleError(err),
        complete: () => this.loading.set(false),
      });
  }

  paginationPrev(): void {
    const off = this.offset();
    const lim = this.limit();
    if (off <= 0) return;
    this.search(Math.max(0, off - lim));
  }

  paginationNext(): void {
    const off = this.offset();
    const lim = this.limit();
    const total = this.totalHits();
    if (off + lim >= total) return;
    this.search(off + lim);
  }

  onPerPageChange(newLimit: number): void {
    this.form.patchValue({ limit: newLimit, offset: 0 }, { emitEvent: false });
    this.search(0);
  }

  /** Build PaginationMeta for list-pagination-bar when search result is present. */
  get paginationMeta(): PaginationMeta {
    return {
      current_page: this.currentPage(),
      last_page: this.totalPages() || 1,
      per_page: this.limit(),
      total: this.totalHits(),
    };
  }

  resetForm(): void {
    this.form.reset({
      subject: '',
      from: '',
      to: '',
      since_date: '',
      until_date: '',
      spam_include: 'exclude',
      date_order: 'desc',
      limit: 25,
      offset: 0,
      mail_account_ids: [],
    });
    this.searchResult.set(null);
    this.expandedId.set(null);
    this.fromOptions.set([]);
    this.toOptions.set([]);
    this.search(0);
  }

  togglePreview(mail: MailHit): void {
    const id = mail.id;
    this.expandedId.set(this.expandedId() === id ? null : id);
  }

  isExpanded(mail: MailHit): boolean {
    return this.expandedId() === mail.id;
  }

  formatMailDate(mail: MailHit): string {
    const unix = mail.Date_Unix;
    if (unix == null) return mail.Date ?? '';
    return (
      this.datePipe.transform(unix * 1000, this.displayDateTimeFormat) ?? ''
    );
  }

  downloadEml(mailId: string, event: Event): void {
    event.preventDefault();
    if (this.downloadingEmlId()) return;
    this.downloadingEmlId.set(mailId);
    this.mailService.downloadEml(mailId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err) => {
          this.downloadingEmlId.set(null);
          this.httpErrors.handleError(err);
        },
        complete: () => this.downloadingEmlId.set(null),
      });
  }

  downloadAttachment(mailId: string, name: string, event: Event): void {
    event.preventDefault();
    const key = `${mailId}|${name}`;
    if (this.downloadingAttachmentKey()) return;
    this.downloadingAttachmentKey.set(key);
    this.mailService.downloadAttachment(mailId, name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err) => {
          this.downloadingAttachmentKey.set(null);
          this.httpErrors.handleError(err);
        },
        complete: () => this.downloadingAttachmentKey.set(null),
      });
  }

  isDownloadingEml(mailId: string): boolean {
    return this.downloadingEmlId() === mailId;
  }

  isDownloadingAttachment(mailId: string, name: string): boolean {
    return this.downloadingAttachmentKey() === `${mailId}|${name}`;
  }

  getAttachmentHit(mail: MailHit, att: string): boolean {
    const content = mail.Attachment_Content ?? {};
    const sub = (this.form.get('subject')?.value ?? '').toLowerCase();
    if (!sub) return false;
    const snippet = (content[att] ?? '').toLowerCase();
    return snippet.includes(sub);
  }

  /** Highlights search term in text; returns SafeHtml for [innerHTML]. */
  highlightSearchTerm(text: string | undefined, searchTerm: string): SafeHtml {
    if (!text || !searchTerm) return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(text ?? ''));
    const sub = searchTerm.toLowerCase();
    const lower = text.toLowerCase();
    const idx = lower.indexOf(sub);
    if (idx === -1) return this.sanitizer.bypassSecurityTrustHtml(this.escapeHtml(text));
    const before = this.escapeHtml(text.slice(0, idx));
    const match = this.escapeHtml(text.slice(idx, idx + searchTerm.length));
    const after = this.escapeHtml(text.slice(idx + searchTerm.length));
    return this.sanitizer.bypassSecurityTrustHtml(before + '<mark>' + match + '</mark>' + after);
  }

  private escapeHtml(s: string): string {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }

  hasSubjectHit(mail: MailHit): boolean {
    const sub = (this.form.get('subject')?.value ?? '').toLowerCase();
    if (!sub) return false;
    return (mail.Subject ?? '').toLowerCase().includes(sub);
  }

  hasFromHit(mail: MailHit): boolean {
    const sub = (this.form.get('subject')?.value ?? '').toLowerCase();
    const from = (this.form.get('from')?.value ?? '').toLowerCase();
    const to = (this.form.get('to')?.value ?? '').toLowerCase();
    const mailFrom = (mail.From ?? '').toLowerCase();
    return (sub && mailFrom.includes(sub)) || (from && mailFrom.includes(from)) || (to && mailFrom.includes(to));
  }

  hasToHit(mail: MailHit): boolean {
    const sub = (this.form.get('subject')?.value ?? '').toLowerCase();
    const from = (this.form.get('from')?.value ?? '').toLowerCase();
    const to = (this.form.get('to')?.value ?? '').toLowerCase();
    const mailTo = (mail.To ?? '').toLowerCase();
    return (sub && mailTo.includes(sub)) || (from && mailTo.includes(from)) || (to && mailTo.includes(to));
  }

  /** Returns the search term to highlight in From field. */
  getFromHighlightTerm(mail: MailHit): string {
    const sub = this.form.get('subject')?.value ?? '';
    const from = this.form.get('from')?.value ?? '';
    const to = this.form.get('to')?.value ?? '';
    const m = (mail.From ?? '').toLowerCase();
    if (sub && m.includes(sub.toLowerCase())) return sub;
    if (from && m.includes(from.toLowerCase())) return from;
    if (to && m.includes(to.toLowerCase())) return to;
    return sub || from || to;
  }

  /** Returns the search term to highlight in To field. */
  getToHighlightTerm(mail: MailHit): string {
    const sub = this.form.get('subject')?.value ?? '';
    const from = this.form.get('from')?.value ?? '';
    const to = this.form.get('to')?.value ?? '';
    const m = (mail.To ?? '').toLowerCase();
    if (sub && m.includes(sub.toLowerCase())) return sub;
    if (from && m.includes(from.toLowerCase())) return from;
    if (to && m.includes(to.toLowerCase())) return to;
    return sub || from || to;
  }

  hasContentHit(mail: MailHit): boolean {
    const sub = (this.form.get('subject')?.value ?? '').toLowerCase();
    if (!sub) return false;
    return (mail.Text ?? '').toLowerCase().includes(sub);
  }
}
